/**
 * Simple modal system.
 */
export default class Modal {
	/**
	 * The main modal element.
	 */
	private modal: HTMLElement = null;

	private prevButton: HTMLElement = null;

	private nextButton: HTMLElement = null;

	private panels: HTMLElement[] = [];

	private currentPanel = 0;

	private keydownHandler;

	/**
	 * Content provided to the modal should either be an element, an array of
	 * elements, or a string (or array of strings) containing one or more
	 * elements. The underlying elements will be moved/created and added to
	 * individual panels of the modal.
	 * @param content Content to display within the modal.
	 */
	constructor(content: string | string[] | HTMLElement | HTMLElement[]) {
		let tempContent: (string | HTMLElement)[];

		// Coerce content to array for single elements.
		if (!Array.isArray(content)) {
			tempContent = [content];
		} else {
			tempContent = content;
		}

		// Create elements from string content.
		tempContent.forEach((panel) => {
			if (typeof panel === 'string') {
				const template = document.createElement('template');
				template.innerHTML = panel;
				const panelClone = document.importNode(template.content, true);
				Array.from(panelClone.children).forEach((clone) => {
					this.panels.push(clone as HTMLElement);
				});
			} else {
				this.panels.push(panel);
			}
		});

		// Create base modal element.
		this.modal = document.createElement('div');
		this.modal.classList.add('modal');
		document.body.appendChild(this.modal);

		// Create the content wrapper.
		const contentWrapper = document.createElement('div');
		contentWrapper.classList.add('modal-wrapper');
		this.modal.appendChild(contentWrapper);

		// Create close button.
		const closeButton = document.createElement('button');
		closeButton.classList.add('modal-button');
		closeButton.classList.add('m-close');
		this.modal.appendChild(closeButton);
		closeButton.addEventListener('click', () => this.close());

		// Only create prev/next buttons if there is more than one panel.
		if (this.panels.length > 1) {
			// Create previous button.
			this.prevButton = document.createElement('button');
			this.prevButton.classList.add('modal-button');
			this.prevButton.classList.add('m-prev');
			this.modal.appendChild(this.prevButton);
			this.prevButton.addEventListener('click', () => this.prevPanel());

			// Create next button.
			this.nextButton = document.createElement('button');
			this.nextButton.classList.add('modal-button');
			this.nextButton.classList.add('m-next');
			this.modal.appendChild(this.nextButton);
			this.nextButton.addEventListener('click', () => this.nextPanel());
		}

		// Wrap content elements in wrappers and append to modal.
		this.panels = this.panels.map((panel) => {
			const wrapper = document.createElement('div');
			wrapper.classList.add('modal-panel');

			// Reset the lazy loading classes on the panel itself
			if (panel.classList.contains('lazyloaded')) {
				panel.classList.remove('lazyloaded');
				panel.classList.add('lazyload');

				const pictures = panel.querySelectorAll('picture');

				Array.from(pictures).forEach((picture) => {
					picture.parentElement.removeChild(picture);
				});
			}

			// Reset the lazy loading classes on the interior elements of the panel
			Array.from(panel.querySelectorAll('.lazyloaded')).forEach((item) => {
				item.classList.remove('lazyloaded');
				item.classList.add('lazyload');

				const pictures = item.querySelectorAll('picture');

				Array.from(pictures).forEach((picture) => {
					picture.parentElement.removeChild(picture);
				});
			});

			// Eager-load all images to prevent flickering from being lazy-loaded
			const images = panel.querySelectorAll('.lazyload');
			Array.from(images).forEach((image) => {
				image.classList.add('lazypreload');
			});

			wrapper.appendChild(panel);
			contentWrapper.appendChild(wrapper);
			return wrapper;
		});

		// Create listener for backdrop click.
		this.modal.addEventListener('click', (e) => {
			if (
				// Return early if the click target is not the backdrop.
				(e.target as HTMLElement).classList.contains('modal-panel')
				|| (e.target as HTMLElement).closest('.modal-panel')
				|| (e.target as HTMLElement).tagName === 'BUTTON'
			) {
				return;
			}

			this.close();
		});

		// Create listener for closing on esc key.
		this.keydownHandler = this.keyboardNavigation.bind(this);
		document.addEventListener('keydown', this.keydownHandler);

		this.showPanel(0);
	}

	keyboardNavigation(e) {
		if (e.key === 'Escape') {
			this.close();
		} else if (e.key === 'ArrowLeft') {
			this.prevPanel();
		} else if (e.key === 'ArrowRight') {
			this.nextPanel();
		}
	}

	nextPanel() {
		this.showPanel(this.currentPanel + 1);
	}

	prevPanel() {
		this.showPanel(this.currentPanel - 1);
	}

	showPanel(newPanel: number) {
		// Normalize index.
		this.currentPanel = ((newPanel % this.panels.length) + this.panels.length) % this.panels.length;
		this.panels.forEach((panel, index) => {
			if (index === this.currentPanel) {
				panel.classList.remove('m-hidden');
			} else {
				panel.classList.add('m-hidden');
			}
		});
	}

	close() {
		this.modal.parentElement.removeChild(this.modal);

		// Remove keydown listener
		document.removeEventListener('keydown', this.keydownHandler);
	}
}
